import { ArrowRight, CheckCircle2, Cloud, Globe, Lock, Mail, MessageSquare, Server, Shield, Users, Zap } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { SiteConfig } from '../api/types'
import { useI18n } from '../i18n'
import { LanguageSelect } from './LanguageSelect'

function useFadeUp() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible') }) },
      { threshold: 0.15 }
    )
    el.querySelectorAll('.fade-up').forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [])
  return ref
}

export function HomePage() {
  const { t } = useI18n()
  const containerRef = useFadeUp()
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null)
  const isLoggedIn = api.isAuthenticated()
  const [displayEmail, setDisplayEmail] = useState(api.getEmail() || '')

  useEffect(() => {
    api.get<SiteConfig>('/site-config').then(setSiteConfig).catch(() => {})
  }, [])

  useEffect(() => {
    if (isLoggedIn && !displayEmail) {
      api.get<{ email: string; username: string }>('/auth/me').then((user) => {
        const label = user.email || user.username || ''
        if (label) {
          setDisplayEmail(label)
          api.setSession(localStorage.getItem('swiss_mail_token') || '', localStorage.getItem('swiss_mail_role') || '', label)
        }
      }).catch(() => {})
    }
  }, [isLoggedIn, displayEmail])

  const features = [
    { icon: Shield, title: 'home.featureSecurity', description: 'home.featureSecurityDetail', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    { icon: Zap, title: 'home.featureSpeed', description: 'home.featureSpeedDetail', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    { icon: Lock, title: 'home.featurePrivacy', description: 'home.featurePrivacyDetail', color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { icon: Globe, title: 'home.featureGlobal', description: 'home.featureGlobalDetail', color: 'text-violet-600 bg-violet-50 border-violet-200' },
    { icon: Cloud, title: 'home.featureBackup', description: 'home.featureBackupDetail', color: 'text-cyan-600 bg-cyan-50 border-cyan-200' },
    { icon: MessageSquare, title: 'home.featureSpam', description: 'home.featureSpamDetail', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  ]

  const stats = [
    { value: '99.9%', label: 'home.statUptime' },
    { value: '<1s', label: 'home.statDelivery' },
    { value: '256-bit', label: 'home.statEncryption' },
    { value: '24/7', label: 'home.statSupport' },
  ]

  const steps = [
    { num: '01', title: 'home.step1Title', desc: 'home.step1Desc', icon: Users },
    { num: '02', title: 'home.step2Title', desc: 'home.step2Desc', icon: Server },
    { num: '03', title: 'home.step3Title', desc: 'home.step3Desc', icon: Mail },
  ]

  return (
    <div className="min-h-screen bg-background" ref={containerRef}>
      {/* Animated decorative background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-primary/5 blur-3xl animate-float" />
        <div className="absolute top-1/3 -left-40 h-[400px] w-[400px] rounded-full bg-primary/3 blur-3xl animate-float" style={{ animationDelay: '-2s' }} />
        <div className="absolute bottom-0 right-1/4 h-[300px] w-[300px] rounded-full bg-primary/4 blur-3xl animate-float" style={{ animationDelay: '-4s' }} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-card/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            {siteConfig?.logoUrl ? (
              <img src={siteConfig.logoUrl} alt="" className="h-9 w-9 rounded-xl object-cover shadow-md" />
            ) : (
              <span className="flex items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 p-2.5 text-primary-foreground shadow-md shadow-primary/20 animate-glow">
                <Mail size={20} />
              </span>
            )}
            <span className="text-lg font-bold tracking-tight">{t('common.appName')}</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSelect />
            {isLoggedIn ? (
              <Link to="/user/dashboard" className="btn-secondary flex items-center gap-1.5 h-9 px-4 text-sm">
                <Mail size={14} />
                {displayEmail || t('user.mail')}
              </Link>
            ) : (
              <Link to="/user/login" className="btn-secondary flex items-center gap-1.5 h-9 px-4 text-sm">
                {t('common.signIn')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="relative">
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-32 text-center">
          <div className="mx-auto max-w-3xl slide-up">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border bg-card/80 px-5 py-2 text-sm text-muted-foreground shadow-sm backdrop-blur-sm border-glow">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10"><Mail size={12} className="text-primary" /></span>
              {t('home.badge')}
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl bg-gradient-to-r from-foreground via-primary/80 to-foreground bg-clip-text text-transparent shimmer-text">
              {t('home.heroTitle')}
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base text-muted-foreground sm:text-lg leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link to="/user/login" className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center h-12 px-8 text-base shadow-lg shadow-primary/20 group">
                {t('home.getStarted')} <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/user/register" className="btn-secondary flex items-center gap-2 w-full sm:w-auto justify-center h-12 px-8 text-base">
                {t('common.register')}
              </Link>
            </div>
          </div>

          {/* Floating decorative orbs around hero */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-[10%] h-3 w-3 rounded-full bg-primary/30 animate-orbit" />
            <div className="absolute top-40 right-[15%] h-2 w-2 rounded-full bg-primary/20 animate-orbit" style={{ animationDelay: '-7s', animationDuration: '15s' }} />
            <div className="absolute bottom-32 left-[20%] h-2.5 w-2.5 rounded-full bg-primary/25 animate-orbit" style={{ animationDelay: '-3s', animationDuration: '25s' }} />
          </div>
        </section>

        {/* Stats bar */}
        <section className="border-y bg-card/50 backdrop-blur-sm fade-up">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-12">
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4 sm:gap-8 stagger">
              {stats.map(({ value, label }) => (
                <div key={label} className="text-center">
                  <p className="text-2xl font-bold tracking-tight sm:text-3xl text-primary">{value}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t(label)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-28">
          <div className="text-center mb-12 sm:mb-16 fade-up">
            <h2 className="text-2xl font-bold tracking-tight sm:text-4xl">{t('home.featuresTitle')}</h2>
            <p className="mt-3 text-muted-foreground sm:text-lg">{t('home.featuresSubtitle')}</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 stagger">
            {features.map(({ icon: Icon, title, description, color }) => (
              <div key={title} className="group panel p-6 sm:p-7 transition-all duration-300 hover:shadow-lg hover:-translate-y-1.5 border border-glow">
                <span className={`inline-flex rounded-xl border p-3 ${color} transition-all duration-300 group-hover:scale-110 group-hover:shadow-md`}>
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 text-base font-semibold">{t(title)}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(description)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="border-y bg-muted/30">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-28">
            <div className="text-center mb-12 sm:mb-16 fade-up">
              <h2 className="text-2xl font-bold tracking-tight sm:text-4xl">{t('home.howTitle')}</h2>
              <p className="mt-3 text-muted-foreground sm:text-lg">{t('home.howSubtitle')}</p>
            </div>
            <div className="grid gap-8 sm:grid-cols-3 stagger">
              {steps.map(({ num, title, desc, icon: Icon }) => (
                <div key={num} className="group relative text-center sm:text-left">
                  <div className="mb-4 inline-flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-hover:shadow-lg group-hover:shadow-primary/20">
                      <Icon size={26} />
                    </span>
                  </div>
                  <span className="absolute -top-2 -left-2 sm:left-auto sm:-top-3 sm:right-4 text-5xl font-extrabold text-primary/10 transition-colors duration-300 group-hover:text-primary/20">{num}</span>
                  <h3 className="text-base font-semibold">{t(title)}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(desc)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Highlights / checklist */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-28">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="fade-up">
              <h2 className="text-2xl font-bold tracking-tight sm:text-4xl">{t('home.whyTitle')}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{t('home.whySubtitle')}</p>
              <ul className="mt-8 space-y-4">
                {['home.why1', 'home.why2', 'home.why3', 'home.why4', 'home.why5'].map((key, i) => (
                  <li key={key} className="flex items-start gap-3 group" style={{ transitionDelay: `${i * 100}ms` }}>
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-primary transition-transform duration-300 group-hover:scale-125" />
                    <span className="text-sm leading-relaxed">{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative fade-up">
              <div className="panel p-8 sm:p-10 border bg-gradient-to-br from-card to-muted/30 border-glow animate-float" style={{ animationDuration: '8s' }}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md animate-glow"><Mail size={20} /></span>
                  <div>
                    <p className="font-semibold">{t('common.appName')}</p>
                    <p className="text-xs text-muted-foreground">{t('home.cardSubtitle')}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg bg-background/80 p-3 border transition-all duration-200 hover:bg-background hover:shadow-sm">
                    <span className="text-sm">{t('home.cardInbox')}</span>
                    <span className="badge bg-primary/10 text-primary">12</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-background/80 p-3 border transition-all duration-200 hover:bg-background hover:shadow-sm">
                    <span className="text-sm">{t('home.cardSent')}</span>
                    <span className="badge bg-muted text-muted-foreground">48</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-background/80 p-3 border transition-all duration-200 hover:bg-background hover:shadow-sm">
                    <span className="text-sm">{t('home.cardDrafts')}</span>
                    <span className="badge bg-muted text-muted-foreground">3</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full rounded-xl bg-primary/5 border border-primary/10" />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-20 sm:py-24 text-center fade-up">
            <h2 className="text-2xl font-bold tracking-tight sm:text-4xl">{t('home.ctaTitle')}</h2>
            <p className="mx-auto mt-3 max-w-lg text-muted-foreground">{t('home.ctaSubtitle')}</p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link to="/user/register" className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center h-12 px-8 text-base shadow-lg shadow-primary/20 group">
                {t('home.ctaButton')} <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t bg-card/50 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-10">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center rounded-lg bg-primary p-1.5 text-primary-foreground"><Mail size={14} /></span>
              <span className="text-sm font-semibold">{t('common.appName')}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('home.footer')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
